import { useCallback, useEffect, useMemo, useRef } from "react";
import { nanoid } from "nanoid/non-secure";
export var componentToLabelSize = {
  small: "small",
  medium: "large",
  large: "large"
};
export var useAutoId = id => useRef(id || nanoid(5)).current;
export var useUpdateEffect = (callback, deps) => {
  var hasMount = useRef(false);
  useEffect(() => {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
export var usePrevious = value => {
  var ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};
export var deprecationWarning = msg => {
  if (typeof window === "object" && window.location.hostname === "localhost" || typeof process === "object" && process.env && process.env.NODE_ENV === "development") {
    console.warn("%c\uD83D\uDEA7 Construct Kit deprecation warning:%c\n" + msg, "font-weight: bold", "font-weight: normal");
  }
};
export var debounce = function (func, _temp) {
  var {
    waitFor = 200,
    leading = false
  } = _temp === void 0 ? {} : _temp;
  var timeout;
  var hasLeadingBeenCalled = false;
  var debounceFunc = function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    if (timeout) {
      clearTimeout(timeout);
    }
    if (leading && !timeout) {
      func(args);
      hasLeadingBeenCalled = true;
    } else {
      hasLeadingBeenCalled = false;
    }
    timeout = window.setTimeout(() => {
      !hasLeadingBeenCalled && func(...args);
    }, waitFor);
  };
  var cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  };
  return [debounceFunc, cancel];
};

// Returns memoised version of debounce function
export var useDebouncedCallback = function (func, waitFor) {
  if (waitFor === void 0) {
    waitFor = 200;
  }
  var funcRef = useRef(func);
  funcRef.current = func;
  var [debounceFunc, cancel] = useMemo(() => debounce(funcRef.current, {
    waitFor
  }), [waitFor]);
  var debouncedCallbackFunc = useCallback(function () {
    return debounceFunc(...arguments);
  }, [debounceFunc]);
  return [debouncedCallbackFunc, cancel];
};
export var replaceToken = (tokens, value) => {
  // improve performance using direct look up for early return
  return tokens[value] || Object.keys(tokens).filter(key => value.includes(key)).reduce((v, t) => v.replace(new RegExp("\\b" + t + "\\b", "g"), tokens[t]), value);
};
export var pxToRem = px => px / 16 + "rem";
var step = (element, startX, startY, endX, endY, startTime) => {
  // Based on polyfill - https://github.com/iamdustan/smoothscroll/blob/master/src/smoothscroll.js
  var MAGIC = 468;
  var elapsedTime = (Date.now() - startTime) / MAGIC;
  var progress = elapsedTime > 1 ? 1 : elapsedTime;
  var multiplier = 0.5 * (1 - Math.cos(Math.PI * progress));
  var nextX = startX + (endX - startX) * multiplier;
  var nextY = startY + (endY - startY) * multiplier;
  element.scrollLeft = nextX;
  element.scrollTop = nextY;
  if (nextX !== endX || nextY !== endY) {
    window.requestAnimationFrame(() => step(element, startX, startY, endX, endY, startTime));
  }
};
export var smoothScrollElement = (element, x, y) => {
  if ("scrollBehavior" in document.documentElement.style) {
    if (x !== null) element.scrollLeft = x;
    if (y !== null) element.scrollTop = y;
  } else {
    var startX = element.scrollLeft;
    var startY = element.scrollTop;
    var endX = x === null ? startX : x;
    var endY = y === null ? startY : y;
    step(element, startX, startY, endX, endY, Date.now());
  }
};
export var transition = properties => {
  if (typeof properties === "string") return properties + " 200ms ease-in";else return "" + properties.map(property => property + " 200ms ease-in");
};